/**
 * ManualVerifyEligibilityDialog
 * ──────────────────────────────
 *
 * Why this exists (May 29 2026 user request):
 *   "Need a way to manually verify insurance coverage — to mark it as
 *    active verified outside the system, by on DOS for DOS."
 *
 * Some payers can't be reliably checked via 270/271 EDI:
 *   - Commonwealth Care Alliance (stale-data quirk; see CCA fix
 *     2026-02-28)
 *   - Brand-new plan IDs not yet propagated in the clearinghouse
 *   - Medicaid Renewals churn
 *   - Out-of-state Blues plans without Availity coverage
 *
 * In all these cases the biller will pick up the phone, call the
 * payer, confirm coverage, and need a way to RECORD that they did
 * — so the next biller doesn't waste time re-verifying, and so the
 * chart reflects the verified state.
 *
 * Effect of clicking Save:
 *   - POST /api/clients/{client_id}/eligibility/manual-verify
 *   - Per-insurance row eligibility_status flips to 'verified'
 *     (or 'inactive' for 'inactive_confirmed')
 *   - Top-level eligibility mirror re-synced via
 *     _sync_top_level_eligibility_status (server.py).
 *   - Writes an eligibility_checks audit row with
 *     is_manual_verification=true so the history timeline
 *     surfaces it with a phone icon (vs. the EDI badge for
 *     automated checks).
 *   - Other insurance rows are NOT touched (independence is a
 *     paired fix shipped same day).
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Phone, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManualVerifyEligibilityDialog({
  open,
  onOpenChange,
  clientId,
  level = 'primary',  // 'primary' | 'secondary' | 'tertiary'
  insuranceName = '',
  defaultDateOfService = '',
  onSuccess = () => {},
}) {
  const [dos, setDos] = useState(
    defaultDateOfService || new Date().toISOString().slice(0, 10)
  );
  const [status, setStatus] = useState('verified');
  const [verifiedWith, setVerifiedWith] = useState('phone');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setDos(defaultDateOfService || new Date().toISOString().slice(0, 10));
    setStatus('verified');
    setVerifiedWith('phone');
    setReference('');
    setNotes('');
  };

  const handleSave = async () => {
    if (!dos) {
      toast.error('Date of service is required.');
      return;
    }
    setSaving(true);
    try {
      const resp = await axios.post(
        `${API}/clients/${clientId}/eligibility/manual-verify`,
        {
          level,
          date_of_service: dos,
          status,
          verified_with: verifiedWith,
          reference: reference.trim() || null,
          notes: notes.trim() || null,
        }
      );
      toast.success(
        status === 'verified'
          ? `${level.charAt(0).toUpperCase() + level.slice(1)} insurance marked verified for ${dos}.`
          : `${level.charAt(0).toUpperCase() + level.slice(1)} insurance marked inactive (confirmed) for ${dos}.`
      );
      reset();
      onOpenChange(false);
      onSuccess(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record manual verification.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manual-verify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-blue-600" />
            Record Manual Eligibility Verification
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            <p>
              Record that you verified <strong>{level}</strong> coverage
              <strong>{insuranceName ? ` (${insuranceName})` : ''}</strong>
              {' '}outside the system — typically via phone, payer
              portal, or fax confirmation.  This does NOT replace a
              270/271 check; it records what you've already done so
              the chart reflects it.
            </p>
          </div>

          {/* Date of Service */}
          <div>
            <Label htmlFor="manual-verify-dos">Date of Service</Label>
            <Input
              id="manual-verify-dos"
              data-testid="manual-verify-dos"
              type="date"
              value={dos}
              onChange={(e) => setDos(e.target.value)}
              className="mt-1"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              The DOS you verified coverage for.  Future-dated check?
              Use the scheduled appointment date.
            </p>
          </div>

          {/* Result */}
          <div>
            <Label>Verification Result</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1" data-testid="manual-verify-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="verified">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Active — confirmed coverage on DOS
                  </span>
                </SelectItem>
                <SelectItem value="inactive_confirmed">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    Inactive — confirmed NO coverage on DOS
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Verified With */}
          <div>
            <Label>Verified Via</Label>
            <Select value={verifiedWith} onValueChange={setVerifiedWith}>
              <SelectTrigger className="mt-1" data-testid="manual-verify-via">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Phone call</SelectItem>
                <SelectItem value="portal">Payer portal</SelectItem>
                <SelectItem value="fax">Fax</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reference */}
          <div>
            <Label htmlFor="manual-verify-ref">Reference / Confirmation #</Label>
            <Input
              id="manual-verify-ref"
              data-testid="manual-verify-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. CCA call ref 12345, CSR Jane"
              className="mt-1"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Optional but recommended — payer call reference number
              or CSR name for audit trail.
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="manual-verify-notes">Notes</Label>
            <Textarea
              id="manual-verify-notes"
              data-testid="manual-verify-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any context the next biller should know"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="manual-verify-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="manual-verify-save"
            className={status === 'verified' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {saving ? 'Saving…' : `Record ${status === 'verified' ? 'Verification' : 'Inactive'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
